import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Row, Col, Card, Button, Table, Badge, Modal, ModalHeader,
  ModalBody, ModalFooter, Form, FormGroup, Label, Input, Alert, Spinner,
  Nav, NavItem, NavLink, TabContent, TabPane
} from 'reactstrap';
import { Plus, RefreshCw, Edit, Trash2, TestTube, Shield, Server, Key, Settings, Wifi, WifiOff, FileText, Target, Square } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API from '../services/api';

const EppAccounts = () => {
  const { user: currentUser } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // Account Settings Modal
  const [accountSettingsModal, setAccountSettingsModal] = useState(false);
  const [selectedAccountForSettings, setSelectedAccountForSettings] = useState(null);
  const [accountSettings, setAccountSettings] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    registrar_category_id: '',
    username: '',
    password: '',
    ssl_cert: '',
    ssl_key: '',
    host_override: '',
    port_override: '',
    manager_id: '',
    connection_timeout: 30,
    is_active: true,
    // Released domain configuration
    released_domain_checking_enabled: false,
    released_domain_schedule_cron: '0 1 * * *',
    released_domain_max_domains_per_day: 10000
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  // Test Catch Modal
  const [testCatchModal, setTestCatchModal] = useState(false);
  const [testCatchAccount, setTestCatchAccount] = useState(null);
  const [testCatchDomains, setTestCatchDomains] = useState('');
  const [testCatchRunning, setTestCatchRunning] = useState(false);
  const [testCatchResults, setTestCatchResults] = useState([]);
  const [pollInterval, setPollInterval] = useState(null);
  const [runningTests, setRunningTests] = useState(new Set()); // Track which accounts have running tests

  // Order-based test catch
  const [testMode, setTestMode] = useState('manual'); // 'manual' or 'orders'
  const [testDate, setTestDate] = useState('');
  const [availableDates, setAvailableDates] = useState([]);

  // Real catch status tracking
  const [runningRealCatches, setRunningRealCatches] = useState(new Set());

  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    loadData();
  }, []);

  const checkRunningTestCatches = async (accounts) => {
    if (!accounts || accounts.length === 0) return;

    try {
      const runningTestsSet = new Set();

      // Check each account for running test catch
      const statusPromises = accounts.map(async (account) => {
        try {
          const response = await API.get(`/epp/accounts/${account.id}/test-catch/status`);
          if (response.data.success && response.data.is_running) {
            runningTestsSet.add(account.id);
            console.log(`Found running test catch for account ${account.id} (${account.name})`);
          }
        } catch (err) {
          // Ignore errors for individual accounts - test catch might not be available
        }
      });

      // Wait for all status checks to complete
      await Promise.all(statusPromises);

      // Update the running tests state
      if (runningTestsSet.size > 0) {
        setRunningTests(runningTestsSet);
        console.log(`Found ${runningTestsSet.size} running test catch sessions:`, Array.from(runningTestsSet));
      }
    } catch (error) {
      console.error('Error checking running test catches:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      // Load accounts
      const accountsResponse = await API.get('/epp/accounts');
      const loadedAccounts = accountsResponse.data.accounts || [];
      setAccounts(loadedAccounts);

      // Check for running test catch sessions and real catch sessions on all accounts
      await checkRunningTestCatches(loadedAccounts);
      await checkRunningRealCatches(loadedAccounts);

      // Load categories
      const categoriesResponse = await API.get('/epp/categories');
      setCategories(categoriesResponse.data.categories || []);

      // Load managers if admin
      if (isAdmin) {
        const managersResponse = await API.get('/managers');
        setManagers(managersResponse.data.managers || []);
      }

    } catch (err) {
      setError('Failed to load data: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    if (type === 'error') {
      setError(message);
      setSuccess('');
    } else {
      setSuccess(message);
      setError('');
    }
    setTimeout(() => {
      setError('');
      setSuccess('');
    }, 5000);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      registrar_category_id: '',
      username: '',
      password: '',
      ssl_cert: '',
      ssl_key: '',
      host_override: '',
      port_override: '',
      manager_id: '',
      connection_timeout: 30,
      is_active: true,
      // Released domain configuration
      released_domain_checking_enabled: false,
      released_domain_schedule_cron: '0 1 * * *',
      released_domain_max_domains_per_day: 10000
    });
    setSelectedAccount(null);
    setEditMode(false);
    setActiveTab('basic');
    setTestResult(null);
  };

  const openModal = (account = null) => {
    if (account) {
      setSelectedAccount(account);
      setEditMode(true);
      setFormData({
        name: account.name || '',
        registrar_category_id: account.registrar_category_id || '',
        username: account.username || '',
        password: '', // Never populate password
        ssl_cert: account.ssl_cert || '',
        ssl_key: '', // Never populate SSL key
        host_override: account.host_override || '',
        port_override: account.port_override || '',
        manager_id: account.manager_id || '',
        connection_timeout: account.connection_timeout || 30,
        is_active: account.is_active !== false,
        // Released domain configuration
        released_domain_checking_enabled: account.released_domain_checking_enabled || false,
        released_domain_schedule_cron: account.released_domain_schedule_cron || '0 1 * * *',
        released_domain_max_domains_per_day: account.released_domain_max_domains_per_day || 10000
      });
    } else {
      resetForm();
    }
    setModal(true);
  };

  const closeModal = () => {
    setModal(false);
    resetForm();
  };

  // Account Settings Modal functions
  const openAccountSettings = async (account) => {
    setSelectedAccountForSettings(account);

    try {
      // Load existing account settings
      const response = await API.get(`/epp/account-settings/${account.id}`);
      setAccountSettings(response.data.effective_settings || {});
    } catch (error) {
      // If no settings exist, use defaults
      console.log('Using category defaults for account settings');
      setAccountSettings({});
    }

    setAccountSettingsModal(true);
  };

  const closeAccountSettings = () => {
    setAccountSettingsModal(false);
    setSelectedAccountForSettings(null);
    setAccountSettings({});
  };

  const handleAccountSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAccountSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
    }));
  };

  const handleAccountSettingsSave = async () => {
    try {
      await API.put(`/epp/account-settings/${selectedAccountForSettings.id}`, accountSettings);
      setSuccess('Account settings saved successfully');
      closeAccountSettings();
    } catch (error) {
      setError('Failed to save account settings: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleConnect = async (accountId) => {
    try {
      const response = await API.post(`/epp/accounts/${accountId}/connect`);
      setSuccess(response.data.message || 'Account connected successfully');
      loadData(); // Refresh the accounts list
    } catch (error) {
      setError('Failed to connect account: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDisconnect = async (accountId) => {
    try {
      const response = await API.post(`/epp/accounts/${accountId}/disconnect`);
      setSuccess(response.data.message || 'Account disconnected successfully');
      loadData(); // Refresh the accounts list
    } catch (error) {
      setError('Failed to disconnect account: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const submitData = {
        ...formData,
        port_override: formData.port_override ? parseInt(formData.port_override) : null,
        connection_timeout: parseInt(formData.connection_timeout)
      };

      if (editMode) {
        // Don't submit empty password/ssl_key fields on edit
        if (!submitData.password) delete submitData.password;
        if (!submitData.ssl_key) delete submitData.ssl_key;

        await API.put(`/epp/accounts/${selectedAccount.id}`, submitData);
        showAlert('success', 'EPP account updated successfully');
      } else {
        await API.post('/epp/accounts', submitData);
        showAlert('success', 'EPP account created successfully');
      }

      closeModal();
      loadData();
    } catch (err) {
      showAlert('error', 'Failed to save account: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this EPP account?')) {
      return;
    }

    try {
      await API.delete(`/epp/accounts/${accountId}`);
      showAlert('success', 'EPP account deleted successfully');
      loadData();
    } catch (err) {
      showAlert('error', 'Failed to delete account: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleTest = async (accountId) => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await API.post(`/epp/accounts/${accountId}/test`);
      setTestResult(response.data);
    } catch (err) {
      setTestResult({
        success: false,
        error: err.response?.data?.error || 'Connection test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  // Test Catch Handlers
  // Load available dates for order-based testing
  const loadAvailableDates = async (accountId) => {
    try {
      const response = await API.get(`/epp/accounts/${accountId}/test-catch/available-dates`);
      if (response.data.success) {
        setAvailableDates(response.data.dates || []);
      }
    } catch (err) {
      console.error('Failed to load available dates:', err);
      setAvailableDates([]);
    }
  };

  const openTestCatchModal = async (account) => {
    setTestCatchAccount(account);

    // Load available dates for order-based testing
    await loadAvailableDates(account.id);

    // Check if test is already running for this account
    try {
      const response = await API.get(`/epp/accounts/${account.id}/test-catch/status`);
      if (response.data.success && response.data.is_running) {
        // Test is already running - restore state (but don't start polling until modal is open)
        setTestCatchRunning(true);
        setTestCatchResults(response.data.results || []);
        setRunningTests(prev => new Set(prev).add(account.id)); // Add to running tests
      } else {
        // No running test - clear state for new test
        setTestCatchRunning(false);
        setTestCatchDomains('');
        setTestCatchResults([]);
        setRunningTests(prev => {
          const newSet = new Set(prev);
          newSet.delete(account.id); // Remove from running tests
          return newSet;
        });
        setTestMode('manual');
        setTestDate('');
      }
    } catch (err) {
      // Error checking status - assume no running test
      setTestCatchRunning(false);
      setTestCatchDomains('');
      setTestCatchResults([]);
    }

    setTestCatchModal(true);

    // Start polling if test is running
    if (testCatchRunning) {
      startPolling(account.id);
    }
  };

  const closeTestCatchModal = () => {
    // Don't stop the test when closing modal - test continues running in background
    if (pollInterval) {
      clearInterval(pollInterval);
      setPollInterval(null);
    }
    setTestCatchModal(false);
    // Don't clear testCatchAccount, testCatchDomains, testCatchResults to preserve state
    // Only clear the modal visibility
  };

  const handleStartTestCatch = async () => {
    if (!testCatchDomains.trim()) {
      showAlert('error', 'Please enter at least one domain');
      return;
    }

    const domains = testCatchDomains
      .split('\n')
      .map(d => d.trim())
      .filter(d => d);

    if (domains.length === 0) {
      showAlert('error', 'Please enter valid domains');
      return;
    }

    setTestCatchRunning(true);
    setTestCatchResults([]);

    try {
      const response = await API.post(`/epp/accounts/${testCatchAccount.id}/test-catch/start`, {
        domains: domains
      });

      if (response.data.success) {
        setRunningTests(prev => new Set(prev).add(testCatchAccount.id)); // Add to running tests
        showAlert('success', 'Test catch started successfully');
        // Start polling for results
        startPolling();
      }
    } catch (err) {
      showAlert('error', 'Failed to start test catch: ' + (err.response?.data?.error || err.message));
      setTestCatchRunning(false);
    }
  };

  const handleStopTestCatch = async () => {
    try {
      await API.post(`/epp/accounts/${testCatchAccount.id}/test-catch/stop`);
      setTestCatchRunning(false);
      showAlert('success', 'Test catch stopped');
      if (pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
      // Clear test catch state when actually stopping
      setRunningTests(prev => {
        const newSet = new Set(prev);
        newSet.delete(testCatchAccount.id); // Remove from running tests
        return newSet;
      });
      setTestCatchAccount(null);
      setTestCatchDomains('');
      setTestCatchResults([]);
    } catch (err) {
      showAlert('error', 'Failed to stop test catch: ' + (err.response?.data?.error || err.message));
    }
  };

  // Start order-based test catch
  const startOrderBasedTestCatch = async () => {
    if (!testDate) {
      showAlert('error', 'Please select a test date');
      return;
    }

    setTestCatchRunning(true);
    setTestCatchResults([]);

    try {
      const response = await API.post(`/epp/accounts/${testCatchAccount.id}/test-catch/start-orders`, {
        test_date: testDate
      });

      if (response.data.success) {
        setRunningTests(prev => new Set(prev).add(testCatchAccount.id)); // Add to running tests
        showAlert('success', `Order-based test catch started for ${testDate}`);
        // Start polling for results
        startPolling();
      }
    } catch (err) {
      showAlert('error', 'Failed to start order-based test catch: ' + (err.response?.data?.error || err.message));
      setTestCatchRunning(false);
    }
  };

  // Check real catch status for accounts
  const checkRunningRealCatches = async (accounts) => {
    if (!accounts || accounts.length === 0) return;
    try {
      const runningRealCatchesSet = new Set();
      const statusPromises = accounts.map(async (account) => {
        try {
          const response = await API.get(`/epp/accounts/${account.id}/real-catch/status`);
          if (response.data.success && response.data.is_running) {
            runningRealCatchesSet.add(account.id);
          }
        } catch (err) {
          // Ignore errors - account might not have real catch capability
        }
      });
      await Promise.all(statusPromises);
      setRunningRealCatches(runningRealCatchesSet);
    } catch (err) {
      console.error('Failed to check real catch status:', err);
    }
  };

  // Start manual real catch
  const startManualRealCatch = async (account, targetDate) => {
    try {
      const response = await API.post(`/epp/accounts/${account.id}/real-catch/start-manual`, {
        target_date: targetDate
      });
      if (response.data.success) {
        showAlert('success', `Real catch started for ${targetDate}`);
        setRunningRealCatches(prev => new Set(prev).add(account.id));
        // Refresh status after a delay
        setTimeout(() => checkRunningRealCatches([account]), 2000);
      }
    } catch (err) {
      showAlert('error', 'Failed to start real catch: ' + (err.response?.data?.error || err.message));
    }
  };

  // Stop real catch
  const stopRealCatch = async (account) => {
    try {
      const response = await API.post(`/epp/accounts/${account.id}/real-catch/stop`);
      if (response.data.success) {
        showAlert('success', 'Real catch stopped successfully');
        setRunningRealCatches(prev => {
          const newSet = new Set(prev);
          newSet.delete(account.id);
          return newSet;
        });
      }
    } catch (err) {
      showAlert('error', 'Failed to stop real catch: ' + (err.response?.data?.error || err.message));
    }
  };

  const startPolling = (accountId = null) => {
    // Use provided accountId or fall back to testCatchAccount.id
    const targetAccountId = accountId || testCatchAccount?.id;

    if (!targetAccountId) {
      console.error('Cannot start polling: no account ID available');
      return;
    }

    const interval = setInterval(async () => {
      try {
        const response = await API.get(`/epp/accounts/${targetAccountId}/test-catch/status`);
        if (response.data.success) {
          // Only update results if polling for the current modal's account
          if (testCatchAccount?.id === targetAccountId) {
            setTestCatchResults(response.data.results || []);
          }

          // Stop polling if test catch is no longer running
          if (!response.data.is_running) {
            if (testCatchAccount?.id === targetAccountId) {
              setTestCatchRunning(false);
            }
            setRunningTests(prev => {
              const newSet = new Set(prev);
              newSet.delete(targetAccountId); // Remove from running tests
              return newSet;
            });
            clearInterval(interval);
            setPollInterval(null);
          }
        }
      } catch (err) {
        console.error('Failed to poll test catch status:', err);
      }
    }, 1000); // Poll every second

    setPollInterval(interval);
  };

  const getSelectedCategory = () => {
    return categories.find(cat => cat.id === parseInt(formData.registrar_category_id));
  };

  const selectedCategory = getSelectedCategory();
  const requiresSSL = selectedCategory?.requires_ssl_cert || false;

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2 className="page-heading">
            <i className="fas fa-server page-heading-icon"></i>
            EPP Accounts
          </h2>
          <small className="text-muted">Manage EPP registrar account credentials and settings</small>
        </Col>
        <Col xs="auto">
          <Button color="secondary" size="sm" onClick={loadData} className="me-2" disabled={loading}>
            <RefreshCw size={16} className="me-1" />
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
          <Button color="primary" size="sm" onClick={() => openModal()}>
            <Plus size={16} className="me-1" /> Add Account
          </Button>
        </Col>
      </Row>

      <Card className="bg-dark border-secondary">
        <div className="card-body">
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {error}
                  <button type="button" className="btn-close-enhanced" onClick={() => setError('')}>×</button>
                </div>
              )}

              {success && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  {success}
                  <button type="button" className="btn-close-enhanced" onClick={() => setSuccess('')}>×</button>
                </div>
              )}

              {testResult && (
                <div className={`alert alert-${testResult.success ? 'success' : 'danger'} alert-dismissible fade show`} role="alert">
                  <strong>Connection Test:</strong> {testResult.success ? 'Success' : 'Failed'}
                  {testResult.error && <div className="mt-2">{testResult.error}</div>}
                  <button type="button" className="btn-close-enhanced" onClick={() => setTestResult(null)}>×</button>
                </div>
              )}

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : accounts.length === 0 ? (
                <div className="text-center py-5">
                  <h5 className="text-muted">No EPP Accounts</h5>
                  <p className="text-muted">Create your first EPP account to get started.</p>
                </div>
              ) : (
                <Table dark hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Username</th>
                      {isAdmin && <th>Manager</th>}
                      <th>SSL</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map(account => (
                      <tr key={account.id}>
                        <td>
                          <strong>{account.name}</strong>
                          {account.host_override && (
                            <small className="d-block text-muted">
                              Host: {account.host_override}
                              {account.port_override && `:${account.port_override}`}
                            </small>
                          )}
                        </td>
                        <td>
                          {account.registrar_category ? (
                            <div>
                              <div className="d-flex align-items-center">
                                <Server size={14} className="text-muted me-2" />
                                <span>{account.registrar_category.name}</span>
                              </div>
                              <small className="text-muted">{account.registrar_category.epp_server_url}</small>
                            </div>
                          ) : (
                            <span className="text-muted">Unknown</span>
                          )}
                        </td>
                        <td>
                          <code className="small">{account.username}</code>
                        </td>
                        {isAdmin && (
                          <td>
                            {account.manager ? (
                              <span>{account.manager.company_name}</span>
                            ) : (
                              <span className="text-muted">No Manager</span>
                            )}
                          </td>
                        )}
                        <td>
                          {account.ssl_cert_configured ? (
                            <Badge className="bg-dark text-success border border-success">
                              <Shield size={12} className="me-1" />
                              Configured
                            </Badge>
                          ) : (
                            <Badge className="bg-dark text-light border border-secondary">Not Set</Badge>
                          )}
                        </td>
                        <td>
                          {!account.is_active ? (
                            <Badge className="bg-dark text-danger border border-danger">Inactive</Badge>
                          ) : account.is_connected ? (
                            <Badge className="bg-dark text-warning border border-warning">Connected</Badge>
                          ) : (
                            <Badge className="bg-dark text-success border border-success">Active</Badge>
                          )}
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <Button
                              color="outline-info"
                              size="sm"
                              onClick={() => handleTest(account.id)}
                              disabled={testing}
                              title="Test Connection"
                              className="me-1"
                            >
                              <TestTube size={14} />
                            </Button>
                            {account.is_connected ? (
                              <Button
                                color="outline-secondary"
                                size="sm"
                                onClick={() => handleDisconnect(account.id)}
                                title="Disconnect Background Connection"
                                className="me-1"
                              >
                                <WifiOff size={14} />
                              </Button>
                            ) : (
                              <Button
                                color="outline-success"
                                size="sm"
                                onClick={() => handleConnect(account.id)}
                                title="Connect Background Connection"
                                className="me-1"
                              >
                                <Wifi size={14} />
                              </Button>
                            )}
                            <Button
                              color={runningTests.has(account.id) ? "secondary" : "outline-secondary"}
                              size="sm"
                              onClick={() => openTestCatchModal(account)}
                              title={runningTests.has(account.id) ? "Test Catch Running - Click to View" : "Test Catch"}
                              className="me-1"
                              style={runningTests.has(account.id) ? { backgroundColor: '#6f42c1', borderColor: '#6f42c1' } : {}}
                            >
                              {runningTests.has(account.id) ? (
                                <div className="d-flex align-items-center">
                                  <Target size={14} className="me-1" />
                                  <span className="spinner-border spinner-border-sm" role="status" style={{ width: '0.5rem', height: '0.5rem' }}>
                                    <span style={{ display: 'none' }}>Loading...</span>
                                  </span>
                                </div>
                              ) : (
                                <Target size={14} />
                              )}
                            </Button>

                            {/* Real Catch Button */}
                            <Button
                              color={runningRealCatches.has(account.id) ? "danger" : "outline-danger"}
                              size="sm"
                              onClick={() => runningRealCatches.has(account.id) ? stopRealCatch(account) : startManualRealCatch(account, '2025-09-28')}
                              title={runningRealCatches.has(account.id) ? "Stop Real Catch" : "Manual Real Catch"}
                              className="me-1"
                              style={runningRealCatches.has(account.id) ? { backgroundColor: '#dc3545', borderColor: '#dc3545' } : {}}
                            >
                              {runningRealCatches.has(account.id) ? (
                                <Square size={14} />
                              ) : (
                                <Target size={14} />
                              )}
                            </Button>
                            <Button
                              color="outline-warning"
                              size="sm"
                              onClick={() => openAccountSettings(account)}
                              title="Account Settings"
                              className="me-1"
                            >
                              <Settings size={14} />
                            </Button>
                            <Button
                              tag={Link}
                              to={`/epp-logs?registrar_name=${encodeURIComponent(account.registrar_category.name)}`}
                              color="outline-secondary"
                              size="sm"
                              title="View EPP Logs for this Registrar"
                              className="me-1"
                            >
                              <FileText size={14} />
                            </Button>
                            <Button
                              color="outline-primary"
                              size="sm"
                              onClick={() => openModal(account)}
                              title="Edit"
                              className="me-1"
                            >
                              <Edit size={14} />
                            </Button>
                            <Button
                              color="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(account.id)}
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </Card>

      {/* Add/Edit Modal */}
      <Modal isOpen={modal} toggle={closeModal} size="lg" className="modal-dark">
        <ModalHeader toggle={closeModal} className="bg-dark text-white border-secondary">
          <i className="fas fa-server me-2"></i>
          {editMode ? 'Edit EPP Account' : 'Add EPP Account'}
        </ModalHeader>
        <ModalBody className="bg-dark text-white">
          <Form onSubmit={handleSubmit}>
            {/* Tab Navigation */}
            <Nav tabs className="mb-3">
              <NavItem>
                <NavLink
                  className={activeTab === 'basic' ? 'active' : ''}
                  onClick={() => setActiveTab('basic')}
                  style={{ cursor: 'pointer' }}
                >
                  <i className="fas fa-cog me-2"></i>
                  Basic Settings
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === 'ssl' ? 'active' : ''}
                  onClick={() => setActiveTab('ssl')}
                  style={{ cursor: 'pointer' }}
                >
                  <i className="fas fa-shield-alt me-2"></i>
                  SSL Configuration
                  {requiresSSL && <span className="text-danger"> *</span>}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === 'released' ? 'active' : ''}
                  onClick={() => setActiveTab('released')}
                  style={{ cursor: 'pointer' }}
                >
                  <i className="fas fa-download me-2"></i>
                  Released Domains
                </NavLink>
              </NavItem>
            </Nav>

            {/* Tab Content */}
            <TabContent activeTab={activeTab}>
              <TabPane tabId="basic">
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="name">
                        Account Name <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="e.g., GoDaddy Main Account"
                        className="bg-secondary border-secondary text-white"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="registrar_category_id">
                        Registrar Category <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="select"
                        id="registrar_category_id"
                        name="registrar_category_id"
                        value={formData.registrar_category_id}
                        onChange={handleInputChange}
                        className="bg-secondary border-secondary text-white"
                        required
                      >
                        <option value="">Select a category...</option>
                        {categories.filter(cat => cat.is_active).map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name} ({category.epp_server_url})
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="username">
                        EPP Username <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        id="username"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        placeholder="epp_username"
                        className="bg-secondary border-secondary text-white"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="password">
                        EPP Password {!editMode && <span className="text-danger">*</span>}
                        {editMode && (
                          <div>
                            <small className="text-muted d-block">(leave empty to keep current)</small>
                            <small className="text-success">
                              <i className="fas fa-check-circle me-1"></i>
                              Current password is set
                            </small>
                          </div>
                        )}
                      </Label>
                      <Input
                        type="password"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder={editMode ? "Leave empty to keep current" : "EPP password"}
                        className="bg-secondary border-secondary text-white"
                        required={!editMode}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {isAdmin && (
                  <FormGroup>
                    <Label for="manager_id">
                      Manager <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="select"
                      id="manager_id"
                      name="manager_id"
                      value={formData.manager_id}
                      onChange={handleInputChange}
                      className="bg-secondary border-secondary text-white"
                      required
                    >
                      <option value="">Select a manager...</option>
                      {managers.map(manager => (
                        <option key={manager.id} value={manager.id}>
                          {manager.company_name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                )}

                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="host_override">
                        Host Override
                        <small className="text-muted">(optional)</small>
                      </Label>
                      <Input
                        type="text"
                        id="host_override"
                        name="host_override"
                        value={formData.host_override}
                        onChange={handleInputChange}
                        placeholder="custom.epp.server.com"
                        className="bg-secondary border-secondary text-white"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="port_override">
                        Port Override
                        <small className="text-muted">(optional)</small>
                      </Label>
                      <Input
                        type="number"
                        id="port_override"
                        name="port_override"
                        value={formData.port_override}
                        onChange={handleInputChange}
                        placeholder="700"
                        min="1"
                        max="65535"
                        className="bg-secondary border-secondary text-white"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="connection_timeout">
                        Timeout (seconds)
                      </Label>
                      <Input
                        type="number"
                        id="connection_timeout"
                        name="connection_timeout"
                        value={formData.connection_timeout}
                        onChange={handleInputChange}
                        min="5"
                        max="300"
                        className="bg-secondary border-secondary text-white"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup check>
                  <Input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  <Label check for="is_active">
                    Account is active
                  </Label>
                </FormGroup>
              </TabPane>

              <TabPane tabId="ssl">
                <div className="mb-3">
                  {requiresSSL ? (
                    <Alert variant="warning">
                      <strong>SSL Certificate Required:</strong> This registrar category requires SSL certificate authentication.
                    </Alert>
                  ) : (
                    <Alert variant="info">
                      <strong>SSL Certificate Optional:</strong> This registrar category supports optional SSL certificate authentication.
                    </Alert>
                  )}
                </div>

                <FormGroup>
                  <Label for="ssl_cert">
                    SSL Certificate
                    {requiresSSL && <span className="text-danger"> *</span>}
                    {editMode && <small className="text-muted d-block">(leave empty to keep current)</small>}
                  </Label>
                  <Input
                    type="textarea"
                    id="ssl_cert"
                    name="ssl_cert"
                    value={formData.ssl_cert}
                    onChange={handleInputChange}
                    placeholder="-----BEGIN CERTIFICATE-----..."
                    rows={6}
                    className="bg-secondary border-secondary text-white"
                    required={requiresSSL && !editMode}
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="ssl_key">
                    SSL Private Key
                    {requiresSSL && <span className="text-danger"> *</span>}
                    {editMode && (
                      <div>
                        <small className="text-muted d-block">(leave empty to keep current)</small>
                        {selectedAccount?.ssl_key_configured ? (
                          <small className="text-success">
                            <i className="fas fa-check-circle me-1"></i>
                            Current private key is set
                          </small>
                        ) : (
                          <small className="text-warning">
                            <i className="fas fa-exclamation-triangle me-1"></i>
                            No private key currently set
                          </small>
                        )}
                      </div>
                    )}
                  </Label>
                  <Input
                    type="textarea"
                    id="ssl_key"
                    name="ssl_key"
                    value={formData.ssl_key}
                    onChange={handleInputChange}
                    placeholder="-----BEGIN PRIVATE KEY-----..."
                    rows={6}
                    className="bg-secondary border-secondary text-white"
                    required={requiresSSL && !editMode}
                  />
                </FormGroup>
              </TabPane>

              {/* Released Domains Tab */}
              <TabPane tabId="released">
                <div className="mb-3">
                  <Alert color="info">
                    <strong>Released Domain Configuration:</strong> Configure automatic checking of domains released from expired domain lists.
                  </Alert>
                </div>

                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="released_domain_checking_enabled">
                        <input
                          type="checkbox"
                          id="released_domain_checking_enabled"
                          checked={formData.released_domain_checking_enabled}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            released_domain_checking_enabled: e.target.checked
                          }))}
                          className="me-2"
                        />
                        Enable Released Domain Checking
                      </Label>
                      <div className="form-text">
                        When enabled, this EPP account will be used to check released domains for availability
                      </div>
                    </FormGroup>
                  </Col>
                </Row>

                {formData.released_domain_checking_enabled && (
                  <>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="released_domain_schedule_cron">
                            Schedule (Cron Expression) <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            id="released_domain_schedule_cron"
                            value={formData.released_domain_schedule_cron}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              released_domain_schedule_cron: e.target.value
                            }))}
                            placeholder="0 1 * * *"
                          />
                          <div className="form-text">
                            Cron expression for when to run domain checks. Default: 0 1 * * * (daily at 1 AM)
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="released_domain_max_domains_per_day">
                            Max Domains Per Day
                          </Label>
                          <Input
                            type="number"
                            id="released_domain_max_domains_per_day"
                            value={formData.released_domain_max_domains_per_day}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              released_domain_max_domains_per_day: parseInt(e.target.value) || 10000
                            }))}
                            min="1"
                            max="100000"
                          />
                          <div className="form-text">
                            Maximum number of domains to check per day (default: 10,000)
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <Alert color="warning">
                          <strong>Important Notes:</strong>
                          <ul className="mb-0 mt-2">
                            <li>Released domain checking uses HIGH priority EPP connections</li>
                            <li>This account's connection pool will be used for batch domain checks</li>
                            <li>Rate limiting will be applied based on registrar delay settings</li>
                            <li>Only one EPP account per registrar should have this enabled</li>
                          </ul>
                        </Alert>
                      </Col>
                    </Row>
                  </>
                )}
              </TabPane>
            </TabContent>
          </Form>
        </ModalBody>
        <ModalFooter className="bg-dark border-secondary">
          <Button color="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit}>
            {editMode ? 'Update Account' : 'Create Account'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Account Settings Modal */}
      {selectedAccountForSettings && (
        <Modal isOpen={accountSettingsModal} toggle={closeAccountSettings} size="xl" className="modal-dark">
          <ModalHeader toggle={closeAccountSettings} className="bg-dark text-white border-secondary">
            <Settings className="me-2" size={20} />
            Account Settings - {selectedAccountForSettings.name}
            <small className="d-block text-muted">Override category defaults for this specific account</small>
          </ModalHeader>
          <ModalBody className="bg-dark text-white">
            <Alert color="info" className="mb-4">
              <strong>Note:</strong> Settings configured here will override the category defaults for this account only.
              Leave fields empty to use category defaults.
            </Alert>

            <Row>
              {/* Scheduling Settings */}
              <Col md={6} className="mb-4">
                <Card className="bg-secondary border-secondary">
                  <div className="card-body">
                    <h6 className="text-light mb-3">
                      <Shield size={16} className="me-2" />
                      Scheduling Settings
                    </h6>

                    <FormGroup>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="connection_schedule_enabled"
                          checked={accountSettings.connection_schedule_enabled || false}
                          onChange={handleAccountSettingsChange}
                        />
                        <label className="form-check-label text-light">
                          Override Connection Schedule
                        </label>
                      </div>
                    </FormGroup>

                    {accountSettings.connection_schedule_enabled && (
                      <Row>
                        <Col md={8}>
                          <FormGroup>
                            <Label>Connection Time (HH:MM:SS)</Label>
                            <Input
                              type="text"
                              name="connection_schedule_time"
                              value={accountSettings.connection_schedule_time || ''}
                              onChange={handleAccountSettingsChange}
                              placeholder="09:00:00"
                              className="bg-dark border-dark text-white"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label>Timezone</Label>
                            <Input
                              type="select"
                              name="connection_schedule_timezone"
                              value={accountSettings.connection_schedule_timezone || 'UTC'}
                              onChange={handleAccountSettingsChange}
                              className="bg-dark border-dark text-white"
                            >
                              <option value="UTC">UTC</option>
                              <option value="Europe/Prague">Europe/Prague</option>
                              <option value="America/New_York">America/New_York</option>
                              <option value="Europe/London">Europe/London</option>
                              <option value="Asia/Tokyo">Asia/Tokyo</option>
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>
                    )}

                    <FormGroup className="mt-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="order_processing_schedule_enabled"
                          checked={accountSettings.order_processing_schedule_enabled || false}
                          onChange={handleAccountSettingsChange}
                        />
                        <label className="form-check-label text-light">
                          Override Order Processing Schedule
                        </label>
                      </div>
                    </FormGroup>

                    {accountSettings.order_processing_schedule_enabled && (
                      <Row>
                        <Col md={4}>
                          <FormGroup>
                            <Label>Start Time (HH:MM:SS)</Label>
                            <Input
                              type="text"
                              name="order_processing_schedule_time"
                              value={accountSettings.order_processing_schedule_time || ''}
                              onChange={handleAccountSettingsChange}
                              placeholder="10:00:00"
                              className="bg-dark border-dark text-white"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label>End Time (HH:MM:SS)</Label>
                            <Input
                              type="text"
                              name="order_processing_end_time"
                              value={accountSettings.order_processing_end_time || ''}
                              onChange={handleAccountSettingsChange}
                              placeholder="18:00:00"
                              className="bg-dark border-dark text-white"
                            />
                            <small className="text-muted">Optional - stop catching at this time</small>
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label>Timezone</Label>
                            <Input
                              type="select"
                              name="order_processing_schedule_timezone"
                              value={accountSettings.order_processing_schedule_timezone || 'UTC'}
                              onChange={handleAccountSettingsChange}
                              className="bg-dark border-dark text-white"
                            >
                              <option value="UTC">UTC</option>
                              <option value="Europe/Prague">Europe/Prague</option>
                              <option value="America/New_York">America/New_York</option>
                              <option value="Europe/London">Europe/London</option>
                              <option value="Asia/Tokyo">Asia/Tokyo</option>
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>
                    )}

                    {/* WHOIS Verification */}
                    <FormGroup className="mt-4">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          name="whois_check_enabled"
                          checked={accountSettings.whois_check_enabled || false}
                          onChange={handleAccountSettingsChange}
                        />
                        <label className="form-check-label text-light">
                          Enable WHOIS Verification (checks every 2 minutes during catch)
                        </label>
                      </div>
                    </FormGroup>

                    {accountSettings.whois_check_enabled && (
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label>WHOIS Server</Label>
                            <Input
                              type="text"
                              name="whois_server"
                              value={accountSettings.whois_server || 'whois.verisign-grs.com'}
                              onChange={handleAccountSettingsChange}
                              placeholder="whois.verisign-grs.com"
                              className="bg-dark border-dark text-white"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Valid Registrar Name</Label>
                            <Input
                              type="text"
                              name="whois_valid_registrar"
                              value={accountSettings.whois_valid_registrar || ''}
                              onChange={handleAccountSettingsChange}
                              placeholder="Gransy, s.r.o."
                              className="bg-dark border-dark text-white"
                            />
                            <small className="text-muted">Registrar name to verify in WHOIS</small>
                          </FormGroup>
                        </Col>
                      </Row>
                    )}
                  </div>
                </Card>
              </Col>

              {/* Workflow Settings */}
              <Col md={6} className="mb-4">
                <Card className="bg-secondary border-secondary">
                  <div className="card-body">
                    <h6 className="text-light mb-3">
                      <Server size={16} className="me-2" />
                      Workflow Settings
                    </h6>

                    <FormGroup>
                      <Label>Capture Strategy</Label>
                      <Input
                        type="select"
                        name="capture_strategy"
                        value={accountSettings.capture_strategy || ''}
                        onChange={handleAccountSettingsChange}
                        className="bg-dark border-dark text-white"
                      >
                        <option value="">Use Category Default</option>
                        <option value="check_then_create">Check then Create</option>
                        <option value="direct_create">Direct Create</option>
                      </Input>
                    </FormGroup>

                    <FormGroup>
                      <Label>Command Delay (milliseconds)</Label>
                      <Input
                        type="number"
                        name="command_delay_ms"
                        value={accountSettings.command_delay_ms || ''}
                        onChange={handleAccountSettingsChange}
                        min="0"
                        max="300000"
                        placeholder="Use category default (max 5 minutes)"
                        className="bg-dark border-dark text-white"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Create Mode</Label>
                      <Input
                        type="select"
                        name="create_mode"
                        value={accountSettings.create_mode || ''}
                        onChange={handleAccountSettingsChange}
                        className="bg-dark border-dark text-white"
                      >
                        <option value="">Use Category Default</option>
                        <option value="wait_response">Wait for Response</option>
                        <option value="fire_and_forget">Fire and Forget</option>
                      </Input>
                    </FormGroup>
                  </div>
                </Card>
              </Col>

              {/* Rate Limiting */}
              <Col md={6} className="mb-4">
                <Card className="bg-secondary border-secondary">
                  <div className="card-body">
                    <h6 className="text-light mb-3">
                      <Shield size={16} className="me-2" />
                      Rate Limiting
                    </h6>

                    <FormGroup>
                      <Label>Max Commands per Second</Label>
                      <Input
                        type="number"
                        name="max_commands_per_second"
                        value={accountSettings.max_commands_per_second || ''}
                        onChange={handleAccountSettingsChange}
                        min="1"
                        max="100"
                        placeholder="Use category default"
                        className="bg-dark border-dark text-white"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Max Concurrent Commands</Label>
                      <Input
                        type="number"
                        name="max_concurrent_commands"
                        value={accountSettings.max_concurrent_commands || ''}
                        onChange={handleAccountSettingsChange}
                        min="1"
                        max="50"
                        placeholder="Use category default"
                        className="bg-dark border-dark text-white"
                      />
                    </FormGroup>
                  </div>
                </Card>
              </Col>

              {/* Domain Check Settings */}
              <Col md={6} className="mb-4">
                <Card className="bg-secondary border-secondary">
                  <div className="card-body">
                    <h6 className="text-light mb-3">
                      <FileText size={16} className="me-2" />
                      Domain Check Settings
                    </h6>

                    <FormGroup check>
                      <Input
                        type="checkbox"
                        name="domain_check_multicheck_enabled"
                        checked={accountSettings.domain_check_multicheck_enabled || false}
                        onChange={handleAccountSettingsChange}
                        className="me-2"
                      />
                      <Label check>Override Batch Domain Checking</Label>
                      <small className="text-muted d-block">Override category setting for batch domain checking</small>
                    </FormGroup>

                    {accountSettings.domain_check_multicheck_enabled && (
                      <FormGroup className="mt-3">
                        <Label>Batch Size</Label>
                        <Input
                          type="number"
                          name="domain_check_batch_size"
                          value={accountSettings.domain_check_batch_size || ''}
                          onChange={handleAccountSettingsChange}
                          min="1"
                          max="100"
                          placeholder="Use category default"
                          className="bg-dark border-dark text-white"
                        />
                        <small className="text-muted">Number of domains per check command (1-100)</small>
                      </FormGroup>
                    )}
                  </div>
                </Card>
              </Col>

              {/* Command Settings */}
              <Col md={6} className="mb-4">
                <Card className="bg-secondary border-secondary">
                  <div className="card-body">
                    <h6 className="text-light mb-3">
                      <Key size={16} className="me-2" />
                      Command Settings
                    </h6>

                    <FormGroup>
                      <Label>Check Command Type</Label>
                      <Input
                        type="select"
                        name="check_command_type"
                        value={accountSettings.check_command_type || ''}
                        onChange={handleAccountSettingsChange}
                        className="bg-dark border-dark text-white"
                      >
                        <option value="">Use Category Default</option>
                        <option value="rfc">RFC Standard</option>
                        <option value="custom">Custom Template</option>
                      </Input>
                    </FormGroup>

                    <FormGroup>
                      <Label>Batch Check Command Type</Label>
                      <Input
                        type="select"
                        name="check_batch_command_type"
                        value={accountSettings.check_batch_command_type || ''}
                        onChange={handleAccountSettingsChange}
                        className="bg-dark border-dark text-white"
                      >
                        <option value="">Use Category Default</option>
                        <option value="rfc">RFC Standard</option>
                        <option value="custom">Custom Template</option>
                      </Input>
                    </FormGroup>

                    <FormGroup>
                      <Label>Create Command Type</Label>
                      <Input
                        type="select"
                        name="create_command_type"
                        value={accountSettings.create_command_type || ''}
                        onChange={handleAccountSettingsChange}
                        className="bg-dark border-dark text-white"
                      >
                        <option value="">Use Category Default</option>
                        <option value="rfc">RFC Standard</option>
                        <option value="custom">Custom Template</option>
                      </Input>
                    </FormGroup>
                  </div>
                </Card>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter className="bg-dark border-secondary">
            <Button color="secondary" onClick={closeAccountSettings}>
              Cancel
            </Button>
            <Button color="success" onClick={handleAccountSettingsSave}>
              Save Account Settings
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Test Catch Modal */}
      <Modal
        isOpen={testCatchModal}
        toggle={closeTestCatchModal}
        size="lg"
        className="modal-dark"
      >
        <ModalHeader toggle={closeTestCatchModal}>
          Test Catch - {testCatchAccount?.name}
        </ModalHeader>
        <ModalBody>
          {/* Test Mode Selection */}
          <div className="mb-3">
            <Label className="text-white mb-2">Test Mode</Label>
            <div className="d-flex gap-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="testMode"
                  id="manual"
                  value="manual"
                  checked={testMode === 'manual'}
                  onChange={(e) => setTestMode(e.target.value)}
                  disabled={testCatchRunning}
                />
                <label className="form-check-label text-white" htmlFor="manual">
                  Manual Domains
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="testMode"
                  id="orders"
                  value="orders"
                  checked={testMode === 'orders'}
                  onChange={(e) => setTestMode(e.target.value)}
                  disabled={testCatchRunning}
                />
                <label className="form-check-label text-white" htmlFor="orders">
                  Real Orders (Simulation)
                </label>
              </div>
            </div>
          </div>

          {/* Manual Mode */}
          {testMode === 'manual' && (
            <div className="mb-3">
              <Label className="text-white">
                Domains to Catch
                <small className="d-block text-muted">
                  Enter one domain per line (e.g. example.com)
                </small>
              </Label>
              <Input
                type="textarea"
                value={testCatchDomains}
                onChange={(e) => setTestCatchDomains(e.target.value)}
                placeholder="example.com&#10;test.com&#10;domain.net"
                rows={6}
                className="bg-dark text-white border-secondary"
                disabled={testCatchRunning}
              />
            </div>
          )}

          {/* Order-Based Mode */}
          {testMode === 'orders' && (
            <div className="mb-3">
              <Label className="text-white">
                Test Date
                <small className="d-block text-muted">
                  Select a date to test with real pending orders
                </small>
              </Label>
              <Input
                type="select"
                value={testDate}
                onChange={(e) => setTestDate(e.target.value)}
                className="bg-dark text-white border-secondary"
                disabled={testCatchRunning}
              >
                <option value="">Select a date...</option>
                {availableDates.map(dateInfo => (
                  <option key={dateInfo.date} value={dateInfo.date}>
                    {dateInfo.date} ({dateInfo.count} orders)
                  </option>
                ))}
              </Input>
              {availableDates.length === 0 && (
                <small className="text-muted">
                  No pending orders found for this account's registrar category.
                </small>
              )}
            </div>
          )}

          {testCatchRunning && (
            <Alert color="info" className="bg-dark border-info">
              <div className="d-flex align-items-center">
                <Spinner size="sm" className="me-2" />
                Test catch is running. Following EPP account configuration...
              </div>
            </Alert>
          )}

          {testCatchResults.length > 0 && (
            <div className="mt-3">
              <h6 className="text-white">Results</h6>
              <div className="bg-secondary p-3 rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {testCatchResults.map((result, index) => (
                  <div key={index} className="mb-2 text-white font-monospace" style={{ fontSize: '12px' }}>
                    <strong>{result.timestamp}</strong> - {result.domain}: {result.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {!testCatchRunning ? (
            <>
              {testMode === 'manual' && (
                <Button color="success" onClick={handleStartTestCatch}>
                  <Target size={16} className="me-1" />
                  Start Manual Test Catch
                </Button>
              )}
              {testMode === 'orders' && (
                <Button color="primary" onClick={startOrderBasedTestCatch}>
                  <Target size={16} className="me-1" />
                  Start Order-Based Test Catch
                </Button>
              )}
              <Button color="secondary" onClick={closeTestCatchModal}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button color="danger" onClick={handleStopTestCatch}>
                <Square size={16} className="me-1" />
                Stop Test Catch
              </Button>
              <Button color="secondary" onClick={closeTestCatchModal} disabled>
                Close
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default EppAccounts;